import type { Platform as System } from '@lyrmo/shared/shortcuts';
import type { IconName } from '../../components/Icon';

/**
 * Whether the site offers the desktop apps: the header's download link, the footer's link, the
 * home page's section on them, and their own page, whose address opens the home page meanwhile.
 * Held back for now; true brings them all back. (A boolean, so the checks on it type-check either
 * way.)
 */
export const DESKTOP_OFFERED = false as boolean;

export const platformIds = ['mac-arm64', 'windows-x64', 'linux-x64'] as const;
export type PlatformId = (typeof platformIds)[number];

export interface DesktopPlatform {
  readonly id: PlatformId;
  readonly system: System;
  /** The system's own name, the same in every language. */
  readonly name: string;
  readonly architecture: string;
  /** What the installer is, as its file extension says. */
  readonly format: string;
  readonly icon: IconName;
}

export const desktopPlatforms: readonly DesktopPlatform[] = [
  {
    id: 'mac-arm64',
    system: 'mac',
    name: 'Mac',
    architecture: 'Apple silicon',
    format: 'DMG',
    icon: 'system-mac',
  },
  {
    id: 'windows-x64',
    system: 'windows',
    name: 'Windows',
    architecture: '64-bit',
    format: 'EXE',
    icon: 'system-windows',
  },
  {
    id: 'linux-x64',
    system: 'linux',
    name: 'Linux',
    architecture: '64-bit · X11',
    format: 'AppImage',
    icon: 'system-linux',
  },
];

/** The Chrome extension, offered from the header and its home section. */
export const chromeExtension = {
  icon: 'system-chrome',
  /** Its listing in the Chrome Web Store, which picks the visitor's language itself. */
  url: 'https://chromewebstore.google.com/detail/lyrmo/ankeoddccdjpehlnndkdhpmlboheomnm',
} as const satisfies Pick<DesktopPlatform, 'icon'> & { readonly url: string };

type HintedNavigator = Pick<Navigator, 'userAgent' | 'maxTouchPoints'> & {
  readonly userAgentData?: { readonly platform?: string };
};

/**
 * The desktop system this browser runs on, to offer its installer first. Null on phones,
 * tablets, and Chromebooks, which have no installer, and wherever the browser doesn't say.
 */
export function detectSystem(browser: HintedNavigator = navigator): System | null {
  const hinted = browser.userAgentData?.platform ?? '';
  const agent = browser.userAgent;
  if (/Android|iPhone|iPad|iPod|CrOS/.test(agent) || /Android|Chrome OS|iOS/.test(hinted)) {
    return null;
  }
  if (hinted === 'macOS' || /Macintosh|Mac OS X/.test(agent)) {
    // iPads ask for desktop sites as a Mac, but have a touch screen.
    return browser.maxTouchPoints > 1 ? null : 'mac';
  }
  if (hinted === 'Windows' || agent.includes('Windows')) return 'windows';
  if (hinted === 'Linux' || /Linux|X11/.test(agent)) return 'linux';
  return null;
}

/** Whether this browser can add the Chrome extension: computers and Chromebooks, not phones. */
export function canAddExtension(browser: HintedNavigator = navigator): boolean {
  return (
    detectSystem(browser) !== null ||
    browser.userAgent.includes('CrOS') ||
    browser.userAgentData?.platform === 'Chrome OS'
  );
}
